/**
 * Carrito del menú público. Todo el cálculo parte SIEMPRE de los datos del menú
 * ya cargados (RPC público); nunca de precios recibidos por input libre.
 * El resultado es una solicitud por WhatsApp, no una transacción confirmada.
 */

export interface CartOptionGroupInput {
  id: string;
  name: string;
  selection_type: string;
  options: Array<{ id: string; name: string; price_modifier: number; is_available: boolean }>;
}

export interface CartLineOption {
  groupId: string;
  groupName: string;
  optionId: string;
  optionName: string;
  priceModifier: number;
}

export interface CartLine {
  lineId: string;
  itemId: string;
  itemName: string;
  /** null / 0 / inválido = platillo sin precio publicado ("precio por confirmar"). */
  basePrice: number | null;
  quantity: number;
  options: CartLineOption[];
  note?: string;
}

const safeNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export function isRequiredGroup(selectionType: string): boolean {
  return selectionType === 'single_required';
}

/** Nombres de los grupos obligatorios que aún no tienen selección. */
export function missingRequiredGroups(
  groups: CartOptionGroupInput[] | undefined,
  selection: Record<string, string | undefined>
): string[] {
  return (groups || [])
    .filter((group) => isRequiredGroup(group.selection_type))
    .filter((group) => {
      const chosen = selection[group.id];
      if (!chosen) return true;
      return !group.options.some((option) => option.id === chosen && option.is_available);
    })
    .map((group) => group.name);
}

/** Convierte la selección del cliente en opciones validadas contra el menú. */
export function resolveSelectedOptions(
  groups: CartOptionGroupInput[] | undefined,
  selection: Record<string, string | undefined>
): CartLineOption[] {
  const resolved: CartLineOption[] = [];
  for (const group of groups || []) {
    const chosenId = selection[group.id];
    if (!chosenId) continue;
    const option = group.options.find((candidate) => candidate.id === chosenId && candidate.is_available);
    if (!option) continue;
    resolved.push({
      groupId: group.id,
      groupName: group.name,
      optionId: option.id,
      optionName: option.name,
      priceModifier: safeNumber(option.price_modifier),
    });
  }
  return resolved;
}

/** Tope por platillo: evita cantidades absurdas por toques accidentales. */
export const MAX_LINE_QUANTITY = 99;

/** Cantidad siempre entera, mínimo 1 y máximo MAX_LINE_QUANTITY. */
export function clampQuantity(quantity: unknown): number {
  const parsed = Math.floor(safeNumber(quantity));
  if (!Number.isFinite(parsed) || parsed < 1) return 1;
  return Math.min(MAX_LINE_QUANTITY, parsed);
}

/** ¿El platillo tiene un precio real publicado? Si no, nunca se inventa un monto. */
export function hasKnownPrice(line: Pick<CartLine, 'basePrice'>): boolean {
  const price = Number(line.basePrice);
  return line.basePrice !== null && line.basePrice !== undefined && Number.isFinite(price) && price > 0;
}

export function lineUnitPrice(line: Pick<CartLine, 'basePrice' | 'options'>): number | null {
  if (!hasKnownPrice(line)) return null;
  return safeNumber(line.basePrice) + line.options.reduce((sum, option) => sum + safeNumber(option.priceModifier), 0);
}

export function lineTotal(line: Pick<CartLine, 'basePrice' | 'options' | 'quantity'>): number | null {
  const unit = lineUnitPrice(line);
  if (unit === null) return null;
  return unit * clampQuantity(line.quantity);
}

/** Suma únicamente los platillos con precio publicado. */
export function cartTotal(lines: CartLine[]): number {
  return lines.reduce((sum, line) => sum + (lineTotal(line) ?? 0), 0);
}

/** ¿Hay platillos sin precio? El total se presenta como parcial. */
export function cartHasUnknownPrices(lines: CartLine[]): boolean {
  return lines.some((line) => !hasKnownPrice(line));
}

export function cartItemCount(lines: CartLine[]): number {
  return lines.reduce((sum, line) => sum + clampQuantity(line.quantity), 0);
}

/** Clave estable: mismo platillo con mismas opciones y misma nota se agrupa. */
export function lineSignature(
  itemId: string,
  options: CartLineOption[],
  note?: string
): string {
  const optionKey = options.map((option) => option.optionId).sort().join(',');
  return `${itemId}|${optionKey}|${(note || '').trim().toLowerCase()}`;
}

export function addLine(lines: CartLine[], incoming: Omit<CartLine, 'lineId'>): CartLine[] {
  const signature = lineSignature(incoming.itemId, incoming.options, incoming.note);
  const existing = lines.find((line) => line.lineId === signature);
  if (existing) {
    return lines.map((line) =>
      line.lineId === signature
        ? { ...line, quantity: clampQuantity(clampQuantity(line.quantity) + clampQuantity(incoming.quantity)) }
        : line
    );
  }
  return [...lines, { ...incoming, lineId: signature, quantity: clampQuantity(incoming.quantity) }];
}

export function setLineQuantity(lines: CartLine[], lineId: string, quantity: number): CartLine[] {
  if (Math.floor(safeNumber(quantity)) <= 0) return lines.filter((line) => line.lineId !== lineId);
  return lines.map((line) => (line.lineId === lineId ? { ...line, quantity: clampQuantity(quantity) } : line));
}
